import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { TableContainer, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import {
  SingleClaimTable,
  CLAIM_MODE_VIEW,
  CLAIM_MODE_EDIT,
  CLAIM_MODE_NEW_CLAIM,
  CLAIM_MODE_NEW_ENTITY,
} from '../components/claim/singleClaimTable';
import { PHASE_SCHEMA, PHASE_CLAIM_SCHEMA } from '../schema/phaseSchema';
import {
  GOLDEN_PHASE_AND_CLAIMS,
  SAVE_PHASE_CLAIM,
  OVERRIDE_PHASE_CLAIM,
  INSERT_GOLDEN_PHASE,
} from '../hasura/queries/phase/phaseClaims';

import { ClaimFilters } from '../components/claim/claimFilters';

import { Progress } from '../components/util/progress';

import { Error } from '../components/util/notifications';
import ClaimHistoryTable from '../components/table/claimHistoryTable';

const useStyle = makeStyles({
  root: {
    marginTop: 20,

    '& h3': {
      paddingLeft: 5,
    },
  },
});
const useStyleButton = makeStyles({
  button: { margin: 5, float: 'right' },
  clearButton: {
    '&:hover': {
      backgroundColor: '#DA0000',
    },
    backgroundColor: '#FF0000',
    color: 'white',
    height: 30,
    padding: 5,
    float: 'right',
    margin: 5,
  },
});

const GoldenPhaseView = ({ phaseId, isNewPhase }) => {
  const ignoreEverything = {};
  PHASE_SCHEMA.forEach(row => {
    ignoreEverything[row.columnKey] = 'Ignored';
  });

  const [claimFilter, setClaimFilter] = useState('all');
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [claimStatuses, setClaimStatuses] = useState(isNewPhase ? ignoreEverything : {});
  const [overrides, setOverrides] = useState({});
  //new phase mode has a 0 id
  const [claimMode, setClaimMode] = useState(isNewPhase ? CLAIM_MODE_NEW_ENTITY : CLAIM_MODE_VIEW);
  const tableContatinerClasses = useStyle();
  const buttonClasses = useStyleButton();
  const { loading, error, data, refetch } = useQuery(GOLDEN_PHASE_AND_CLAIMS, {
    variables: {
      phase_id: phaseId,
    },
  });

  if (loading) return <Progress />;
  let allClaims = [];
  data &&
    data.statsbomb_phase &&
    data.statsbomb_phase[0] &&
    data.statsbomb_phase[0].mapping_phases.forEach(mappingEntry => {
      allClaims = [...allClaims, ...mappingEntry.claim_phases];
    });

  let filteredClaims = allClaims;
  if (claimFilter === 'undecided') {
    filteredClaims = allClaims.filter(c => {
      return c.approved_at === null && c.rejected_at === null;
    });
  } else if (claimFilter === 'approved') {
    filteredClaims = allClaims.filter(c => c.approved_at != null);
  } else if (claimFilter === 'rejected') {
    filteredClaims = allClaims.filter(c => c.rejected_at != null);
  }

  const selectAClaim = claimUUID => {
    setSelectedClaimId(claimUUID);
    const currentDecisions = {};

    const claim = allClaims.filter(c => {
      return c.claim_phase_uuid === claimUUID;
    })[0];
    if (claim) {
      PHASE_SCHEMA.forEach(row => {
        if (claim && claim[row.columnKey] != null) {
          currentDecisions[row.columnKey] =
            row.mapping_function && row.mapping_function(claim) == null ? 'Ignored' : 'Approved';
        }
      });
    }
    setClaimStatuses(currentDecisions);

    const initialOverrides = {};
    PHASE_SCHEMA.forEach(row => {
      initialOverrides[row.columnKey] = null;
    });
    setOverrides(initialOverrides);

    setClaimMode(claimUUID ? CLAIM_MODE_EDIT : CLAIM_MODE_VIEW);
  };

  const changeClaimFilter = claimFilter => {
    selectAClaim(null);
    setClaimFilter(claimFilter);
  };

  const newClaim = () => {
    selectAClaim(null);
    setClaimMode(CLAIM_MODE_NEW_CLAIM);
    setClaimStatuses(ignoreEverything);
  };

  const getGoldenPhaseId = insertedPhase => insertedPhase?.data?.insert_statsbomb_phase?.returning?.[0]?.phase_id;

  const claimSelected = claim => {
    selectAClaim(claim.claim_phase_uuid);
  };

  const formatBodyIndentifierValue = () => {
    const {
      statsbomb_phase_type,
      statsbomb_competition_season: { statsbomb_competition, statsbomb_season },
    } = data.statsbomb_phase[0];

    return `${statsbomb_phase_type?.phase_type_name} - ${statsbomb_competition?.competition_name} ${statsbomb_season?.season_name}`;
  };

  return (
    <>
      <Error message="" error={error} />
      <div className="container-row">
        <h2>Phase Singular: {isNewPhase ? ' New Phase' : phaseId}</h2>

        {claimMode !== CLAIM_MODE_NEW_ENTITY && (
          <TableContainer component={Paper}>
            <ClaimFilters claimFilter={claimFilter} filterResultsFn={changeClaimFilter} />
            <ClaimHistoryTable
              entityType="phase"
              schema={PHASE_CLAIM_SCHEMA}
              entities={filteredClaims}
              identifier={{
                headerValue: 'Phase Name',
                bodyValue: data.statsbomb_phase[0] ? formatBodyIndentifierValue() : '',
              }}
              rowClickHandler={claimSelected}
              selectedEntityKey={selectedClaimId}
            />
            <Button className={buttonClasses.button} variant="contained" color="primary" onClick={() => newClaim()}>
              New Claim
            </Button>
          </TableContainer>
        )}

        <TableContainer classes={tableContatinerClasses} component={Paper}>
          <h3>Phase Attributes {selectedClaimId && ' And Claim Values'}</h3>

          <SingleClaimTable
            goldenEntity={data.statsbomb_phase[0]}
            claim={
              allClaims.filter(c => {
                return c.claim_phase_uuid === selectedClaimId;
              })[0]
            }
            rowSchema={PHASE_SCHEMA}
            saveMutation={SAVE_PHASE_CLAIM}
            overrideMutation={OVERRIDE_PHASE_CLAIM}
            createMutation={INSERT_GOLDEN_PHASE}
            sourcePK="provider_phase_id"
            claimPK="claim_phase_uuid"
            goldenEntityPK="phase_id"
            getGoldenEntityId={getGoldenPhaseId}
            entityType="phase"
            setClaimModeView={() => setClaimMode(CLAIM_MODE_VIEW)}
            {...{
              claimStatuses,
              setClaimStatuses,
              selectAClaim,
              overrides,
              setOverrides,
              claimMode,
              refetch,
            }}
          />
        </TableContainer>
      </div>
    </>
  );
};

function Phase(props) {
  const query = new URLSearchParams(useLocation().search);
  const isNewPhase = query.get('new');
  const phaseId = isNewPhase ? '0' : query.get('phase_id');

  return <GoldenPhaseView phaseId={phaseId} isNewPhase={!!isNewPhase} />;
}

export default Phase;
